// Performance optimization utilities for WebGPU reel processing

export class PerformanceOptimizer {
  constructor() {
    this.memoryPool = new Map();
    this.bufferCache = new Map();
    this.performanceMetrics = {
      frameProcessingTimes: [],
      transitionTimes: [],
      totalProcessingTime: 0,
      gpuUtilization: 0
    };
  }

  // Memory pool for reusing GPU buffers
  getPooledBuffer(device, size, usage, label = '') {
    const key = `${size}-${usage}`;
    
    if (this.memoryPool.has(key)) {
      const buffer = this.memoryPool.get(key);
      this.memoryPool.delete(key);
      return buffer;
    }
    
    return device.createBuffer({
      size,
      usage,
      label: label || `pooled-${key}`
    });
  }

  // Return buffer to memory pool for reuse
  returnBufferToPool(buffer, size, usage) {
    const key = `${size}-${usage}`;
    
    // Limit pool size to prevent memory leaks
    if (this.memoryPool.size < 20) {
      this.memoryPool.set(key, buffer);
    } else {
      buffer.destroy();
    }
  }

  // Batch multiple image processing operations
  async batchProcessImages(processor, operations) {
    const results = [];
    const batchSize = 4; // Process 4 images simultaneously
    
    for (let i = 0; i < operations.length; i += batchSize) {
      const batch = operations.slice(i, i + batchSize);
      const batchPromises = batch.map(op => processor.processImageTransition(...op));
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      // Small delay to prevent GPU overload
      if (i + batchSize < operations.length) {
        await this.sleep(10);
      }
    }
    
    return results;
  }

  // Optimize frame generation with smart caching
  async optimizeFrameGeneration(frames, segments, cacheStrategy = 'lru') {
    const frameCache = new Map();
    const optimizedFrames = [];
    
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];
      const segment = segments.find(s => i >= s.startFrame && i < s.endFrame);
      
      if (!segment) {
        optimizedFrames.push(frame);
        continue;
      }
      
      // Cache key based on image and transition state
      const cacheKey = `${segment.imageIndex}-${segment.transitionType}-${Math.floor((i - segment.startFrame) / 10)}`;
      
      if (frameCache.has(cacheKey) && cacheStrategy === 'lru') {
        // Reuse cached frame with minor variations
        const cachedFrame = frameCache.get(cacheKey);
        optimizedFrames.push(this.applyMinorVariation(cachedFrame, i));
      } else {
        optimizedFrames.push(frame);
        
        // Cache this frame
        if (frameCache.size < 50) {
          frameCache.set(cacheKey, frame);
        }
      }
    }
    
    return optimizedFrames;
  }

  // Apply minor variations to cached frames
  applyMinorVariation(baseFrame, frameIndex) {
    // Add subtle noise or adjustment to prevent identical frames
    const variation = new ImageData(baseFrame.width, baseFrame.height);
    const data = new Uint8ClampedArray(baseFrame.data);
    
    // Add minimal random variation (±1 per channel)
    for (let i = 0; i < data.length; i += 4) {
      const noise = (Math.random() - 0.5) * 2;
      data[i] = Math.max(0, Math.min(255, data[i] + noise));     // R
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise)); // G
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise)); // B
      // Keep alpha unchanged
    }
    
    variation.data.set(data);
    return variation;
  }

  // Smart transition selection based on performance
  selectOptimalTransition(beatIntensity, previousTransition, performanceLevel) {
    const transitions = {
      low: ['crossfade', 'wipe'],
      medium: ['crossfade', 'slide', 'wipe', 'zoom'],
      high: ['crossfade', 'slide', 'wipe', 'zoom', 'pixelate', 'ripple'],
      ultra: ['crossfade', 'slide', 'wipe', 'zoom', 'pixelate', 'ripple', 'glitch', 'burn']
    };
    
    const availableTransitions = transitions[performanceLevel] || transitions.medium;
    
    // Weight selection based on intensity
    if (beatIntensity < 0.3) {
      return availableTransitions.includes('crossfade') ? 'crossfade' : availableTransitions[0];
    } else if (beatIntensity < 0.6) {
      const midTransitions = availableTransitions.filter(t => ['slide', 'zoom', 'wipe'].includes(t));
      return midTransitions.length > 0 ? midTransitions[Math.floor(Math.random() * midTransitions.length)] : 'crossfade';
    } else {
      const dramaticTransitions = availableTransitions.filter(t => ['ripple', 'glitch', 'burn', 'pixelate'].includes(t));
      return dramaticTransitions.length > 0 ? dramaticTransitions[Math.floor(Math.random() * dramaticTransitions.length)] : 'zoom';
    }
  }

  // Monitor performance and adjust settings
  updatePerformanceMetrics(frameTime, transitionTime) {
    this.performanceMetrics.frameProcessingTimes.push(frameTime);
    this.performanceMetrics.transitionTimes.push(transitionTime);
    
    // Keep only last 100 measurements
    if (this.performanceMetrics.frameProcessingTimes.length > 100) {
      this.performanceMetrics.frameProcessingTimes.shift();
    }
    if (this.performanceMetrics.transitionTimes.length > 100) {
      this.performanceMetrics.transitionTimes.shift();
    }
  }

  // Get current performance level
  getPerformanceLevel() {
    const avgFrameTime = this.performanceMetrics.frameProcessingTimes.reduce((a, b) => a + b, 0) / this.performanceMetrics.frameProcessingTimes.length;
    const avgTransitionTime = this.performanceMetrics.transitionTimes.reduce((a, b) => a + b, 0) / this.performanceMetrics.transitionTimes.length;
    
    if (avgFrameTime < 16) return 'ultra'; // 60fps+
    if (avgFrameTime < 33) return 'high';  // 30fps+
    if (avgFrameTime < 50) return 'medium'; // 20fps+
    return 'low'; // <20fps
  }

  // Adaptive quality settings
  getAdaptiveQuality() {
    const perfLevel = this.getPerformanceLevel();
    
    return {
      ultra: { imageQuality: 0.95, transitionComplexity: 'high', frameRate: 30 },
      high: { imageQuality: 0.9, transitionComplexity: 'medium', frameRate: 30 },
      medium: { imageQuality: 0.85, transitionComplexity: 'medium', frameRate: 24 },
      low: { imageQuality: 0.8, transitionComplexity: 'low', frameRate: 20 }
    }[perfLevel];
  }

  // Clean up resources
  cleanup() {
    // Destroy all pooled buffers
    for (const buffer of this.memoryPool.values()) {
      if (buffer && buffer.destroy) {
        buffer.destroy();
      }
    }
    this.memoryPool.clear();
    this.bufferCache.clear();
  }

  // Utility sleep function
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Log performance summary
  logPerformanceSummary() {
    const metrics = this.performanceMetrics;
    const avgFrame = metrics.frameProcessingTimes.reduce((a, b) => a + b, 0) / metrics.frameProcessingTimes.length;
    const avgTransition = metrics.transitionTimes.reduce((a, b) => a + b, 0) / metrics.transitionTimes.length;
    
    console.log('Performance Summary:');
    console.log(`Average frame time: ${avgFrame.toFixed(2)}ms`);
    console.log(`Average transition time: ${avgTransition.toFixed(2)}ms`);
    console.log(`Performance level: ${this.getPerformanceLevel()}`);
    console.log(`Total processing time: ${metrics.totalProcessingTime.toFixed(2)}ms`);
  }
}

export default PerformanceOptimizer;